import React, { createContext, useContext, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";

const ModalContext = createContext(null);

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 20;
  padding: 24px;
  overflow: auto;

  @media screen and (max-width: 768px) {
    padding: 0;
  }
`;

const ModalContainer = styled.div`
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
  overflow: auto;
`;

export const ModalProvider = ({ children }) => {
  const [modals, setModals] = useState([]);
  const openModal = useCallback((modalComponent, options = {}) => {
    const modalId = options?.modalId || Date.now().toString();
    const { onClose, ...otherOptions } = options;

    const handleClose = () => {
      setModals((prev) => prev.filter((modal) => modal.id !== modalId));
      onClose?.();
    };

    const modalData = {
      id: modalId,
      component: modalComponent,
      onClose: handleClose,
      ...otherOptions,
    };

    setModals((prev) => [...prev, modalData]);
    return modalId;
  }, []);

  const closeModal = useCallback((modalId) => {
    setModals((prev) => {
      const modal = prev.find((m) => m.id === modalId);
      if (modal) {
        modal.onClose?.();
      }
      return prev.filter((m) => m.id !== modalId);
    });
  }, []);

  const closeAllModals = useCallback(() => {
    modals.forEach((modal) => modal.onClose?.());
    setModals([]);
  }, [modals]);

  const value = {
    openModal,
    closeModal,
    closeAllModals,
    modals,
  };

  // Render portals directly for each modal
  const modalPortals = modals.map((modal) => {
    const handleOverlayClick = (e) => {
      if (e.target === e.currentTarget) {
        modal.onClose?.();
      }
    };

    return createPortal(
      <ModalOverlay
        key={modal.id}
        onClick={handleOverlayClick}
        className={modal.className}
      >
        <ModalContainer onClick={(e) => e.stopPropagation()}>
          {typeof modal.component === "function"
            ? modal.component({ onClose: modal.onClose, ...modal.props })
            : modal.component}
        </ModalContainer>
      </ModalOverlay>,
      document.body,
      modal.id
    );
  });

  return (
    <ModalContext.Provider value={value}>
      {children}
      {modalPortals}
    </ModalContext.Provider>
  );
};

export const useModal = () => {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error("useModal must be used within a ModalProvider");
  }
  return context;
};

export default ModalContext;
